import { Link } from "react-router-dom";
import { SectionHeader } from "../components/UI";
import { ScrollText, ArrowRight, ShieldCheck, Fingerprint } from "lucide-react";

/*
 * Resources , Organization hub. Currently exposes the Policies authoring surface.
 * This page is a lightweight index so new resource types (SOPs, handbooks,
 * training modules) can slot in without changing the top-level nav.
 * Reachable by HR / Company Admin / Super Admin only (route gated in App.js).
 */

const RESOURCE_TYPES = [
  {
    key: "policies",
    Icon: ScrollText,
    tint: "bg-blue-50 text-blue-700 border-blue-200",
    title: "Policies",
    description: "Author, version, and publish the policies employees and managers acknowledge. Approval-gated; every version is SHA-256 fingerprinted.",
    to: "/resources/policies",
    testid: "resource-tile-policies",
    cta: "Open policies",
  },
];

export default function Resources() {
  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="resources-hub">
      <SectionHeader
        title="Resources"
        subtitle="Author and publish the documents your workspace runs on. Choose a resource type below to manage it."
      />

      <div className="grid md:grid-cols-2 gap-4 mt-6">
        {RESOURCE_TYPES.map((r) => (
          <Link
            key={r.key}
            to={r.to}
            className="group border border-slate-200 bg-white p-6 hover:border-slate-900 transition-all rounded"
            data-testid={r.testid}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className={"w-10 h-10 border flex items-center justify-center shrink-0 " + r.tint}>
                <r.Icon size={18} strokeWidth={1.75} />
              </div>
              <ArrowRight size={16} strokeWidth={2} className="text-slate-400 group-hover:text-slate-900 group-hover:translate-x-0.5 transition-all" />
            </div>
            <h3 className="font-display text-lg font-semibold text-slate-900 mb-1 group-hover:text-blue-700 transition-colors">
              {r.title}
            </h3>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">{r.description}</p>
            <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-slate-700 group-hover:text-blue-700 transition-colors">
              {r.cta}
              <ArrowRight size={11} strokeWidth={2} />
            </span>
          </Link>
        ))}
      </div>

      <div className="border border-slate-200 bg-slate-50/60 p-5 mt-8 flex items-start gap-3 rounded">
        <ShieldCheck size={18} className="text-emerald-700 mt-0.5 shrink-0" strokeWidth={1.75} />
        <div className="text-sm text-slate-700 leading-relaxed">
          <div className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
            <Fingerprint size={12} strokeWidth={2} />
            Approval-gated by design
          </div>
          Nothing published from Resources goes live automatically. Company Admin approval is required, and every save writes a tamper-evident SHA-256 fingerprint into the audit log.
        </div>
      </div>
    </div>
  );
}
